import { useState, useEffect } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import { DIRECTIONS } from "./ssr-data";
import styles from "./AddSSRItemModal.module.css";

const emptyForm = {
  code: "",
  description: "",
  category: "",
  maxWeight: "",
  maxSize: "",
  maxPieces: "",
  flightSegmentRequired: true,
  travelerRequired: true,
  direction: "Both",
  status: "active",
};

const AddSSRItemModal = ({ open, onClose, onSave, editingItem, categories = [], apiError = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editingItem) {
      setForm({
        code: editingItem.code || "",
        description: editingItem.description || "",
        category: editingItem.category || "",
        maxWeight: editingItem.maxWeight ?? "",
        maxSize: editingItem.maxSize || "",
        maxPieces: editingItem.maxPieces ?? "",
        flightSegmentRequired: editingItem.flightSegmentRequired !== false,
        travelerRequired: editingItem.travelerRequired !== false,
        direction: editingItem.direction || "Both",
        status: editingItem.status || "active",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [editingItem, open]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "code":
        if (!value || !value.trim()) {
          error = "SSR Code is required";
        } else if (value.length < 2 || value.length > 4) {
          error = "SSR Code must be 2-4 characters";
        } else if (!/^[A-Z0-9]+$/.test(value)) {
          error = "SSR Code must contain only uppercase letters and numbers";
        }
        break;
      case "description":
        if (!value || !value.trim()) {
          error = "Description is required";
        } else if (value.length < 3) {
          error = "Description must be at least 3 characters";
        }
        break;
      case "category":
        if (!value) {
          error = "Category is required";
        }
        break;
      case "maxWeight":
        if (value && (isNaN(value) || Number(value) <= 0)) {
          error = "Max Weight must be a positive number";
        }
        break;
      case "maxPieces":
        if (value && (isNaN(value) || Number(value) <= 0 || !Number.isInteger(Number(value)))) {
          error = "Max Pieces must be a positive integer";
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!form.code.trim()) {
      newErrors.code = "SSR Code is required";
      isValid = false;
    } else if (form.code.length < 2 || form.code.length > 4) {
      newErrors.code = "SSR Code must be 2-4 characters";
      isValid = false;
    } else if (!/^[A-Z0-9]+$/.test(form.code)) {
      newErrors.code = "SSR Code must contain only uppercase letters and numbers";
      isValid = false;
    }

    if (!form.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    } else if (form.description.length < 3) {
      newErrors.description = "Description must be at least 3 characters";
      isValid = false;
    }

    if (!form.category) {
      newErrors.category = "Category is required";
      isValid = false;
    }

    if (form.maxWeight && (isNaN(form.maxWeight) || Number(form.maxWeight) <= 0)) {
      newErrors.maxWeight = "Max Weight must be a positive number";
      isValid = false;
    }

    if (form.maxPieces && (isNaN(form.maxPieces) || Number(form.maxPieces) <= 0 || !Number.isInteger(Number(form.maxPieces)))) {
      newErrors.maxPieces = "Max Pieces must be a positive integer";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }

    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      category: form.category,
      maxWeight: form.maxWeight ? Number(form.maxWeight) : null,
      maxSize: form.maxSize?.trim() || null,
      maxPieces: form.maxPieces ? Number(form.maxPieces) : null,
      flightSegmentRequired: form.flightSegmentRequired,
      travelerRequired: form.travelerRequired,
      direction: form.direction,
      status: form.status,
    };

    try {
      const errorMsg = await onSave(payload);
      if (errorMsg) {
        // Error shown via apiError prop
      } else {
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = categories.find((cat) => cat.code === form.category);
  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some((e) => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={editingItem ? "Edit SSR Item" : "Add SSR Item"}
      width="720px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Changes"}
          </button>
        </>
      }
    >
      {/* Icon Header */}
      <div className={styles.iconHeader}>
        <div
          className={styles.icon}
          style={{
            background: selectedCategory
              ? selectedCategory.color
              : "linear-gradient(135deg, #f5a51e 0%, #ff8c00 100%)",
          }}
        >
          {selectedCategory ? selectedCategory.icon : "🎫"}
        </div>
        <div className={styles.headerText}>
          <h3>SSR Item Details</h3>
          <p>
            {editingItem
              ? "Update special service request configuration"
              : "Create a new special service request"}
          </p>
        </div>
      </div>

      {/* API Error Banner */}
      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Save failed:</strong> {apiError}
          </div>
        </div>
      )}

      {/* Validation Error Banner */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(
                ([key, error]) => error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Basic Information Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📋 BASIC INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>
              SSR CODE <span className={styles.required}>*</span>
            </label>
            <input
              value={form.code}
              onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("code")}
              placeholder="e.g., WCHR, AVML"
              maxLength={4}
              className={`${styles.codeInput} ${errors.code ? styles.inputError : ""}`}
            />
            <span className={styles.hint}>2-4 uppercase characters</span>
            {errors.code && <span className={styles.errorText}>{errors.code}</span>}
          </div>

          <div className={styles.field}>
            <label>
              CATEGORY <span className={styles.required}>*</span>
            </label>
            <select
              value={form.category}
              onChange={(e) => handleChange("category", e.target.value)}
              onBlur={() => handleBlur("category")}
              className={errors.category ? styles.inputError : ""}
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
            {errors.category && (
              <span className={styles.errorText}>{errors.category}</span>
            )}
          </div>

          <div className={styles.fieldWide}>
            <label>
              DESCRIPTION <span className={styles.required}>*</span>
            </label>
            <input
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              onBlur={() => handleBlur("description")}
              placeholder="e.g., Wheelchair - Ramp"
              className={errors.description ? styles.inputError : ""}
            />
            {errors.description && (
              <span className={styles.errorText}>{errors.description}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>STATUS</label>
            <select
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Physical Constraints Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📏 PHYSICAL CONSTRAINTS (OPTIONAL)</h4>
        <p className={styles.sectionDesc}>
          Define maximum limits for weight, size, and quantity if applicable
        </p>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>MAX WEIGHT (KG)</label>
            <input
              type="number"
              value={form.maxWeight}
              onChange={(e) => handleChange("maxWeight", e.target.value)}
              onBlur={() => handleBlur("maxWeight")}
              placeholder="e.g., 23"
              min="0"
              step="0.1"
              className={errors.maxWeight ? styles.inputError : ""}
            />
            {errors.maxWeight && (
              <span className={styles.errorText}>{errors.maxWeight}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>MAX SIZE</label>
            <input
              value={form.maxSize}
              onChange={(e) => handleChange("maxSize", e.target.value)}
              placeholder="e.g., 55x40x23 or 158cm"
            />
            <span className={styles.hint}>Dimensions or linear measurement</span>
          </div>

          <div className={styles.field}>
            <label>MAX PIECES</label>
            <input
              type="number"
              value={form.maxPieces}
              onChange={(e) => handleChange("maxPieces", e.target.value)}
              onBlur={() => handleBlur("maxPieces")}
              placeholder="e.g., 1"
              min="0"
              step="1"
              className={errors.maxPieces ? styles.inputError : ""}
            />
            {errors.maxPieces && (
              <span className={styles.errorText}>{errors.maxPieces}</span>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Rules Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>⚙️ ASSIGNMENT RULES</h4>
        <p className={styles.sectionDesc}>
          Define how this SSR should be assigned to bookings
        </p>

        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>DIRECTION</label>
            <select
              value={form.direction}
              onChange={(e) => handleChange("direction", e.target.value)}
            >
              {DIRECTIONS.map((dir) => (
                <option key={dir} value={dir}>
                  {dir}
                </option>
              ))}
            </select>
            <span className={styles.hint}>Flight direction applicability</span>
          </div>
        </div>

        <div className={styles.checkboxGroup}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={form.flightSegmentRequired}
              onChange={(e) =>
                handleChange("flightSegmentRequired", e.target.checked)
              }
            />
            <span>Flight Segment Assignment Required</span>
          </label>
          <p className={styles.checkboxHint}>
            SSR must be assigned to specific flight segment(s)
          </p>
        </div>

        <div className={styles.checkboxGroup}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={form.travelerRequired}
              onChange={(e) => handleChange("travelerRequired", e.target.checked)}
            />
            <span>Traveler Assignment Required</span>
          </label>
          <p className={styles.checkboxHint}>
            SSR must be assigned to specific traveler(s)
          </p>
        </div>
      </div>

      {/* Info Notice */}
      <div className={styles.infoNotice}>
        <div className={styles.noticeIcon}>💡</div>
        <div className={styles.noticeContent}>
          <strong>Pricing & Status</strong>
          <p>
            SSR pricing and confirmation status are managed at the booking level,
            not in the master data definition.
          </p>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddSSRItemModal;